import React, { useCallback, useEffect, useState } from 'react';

import { ModalHeader } from '../../components/ModalHeader';
import { Icon } from '../../components/Icon';
import { useModal } from '../../hooks/modal';
import { useToast } from '../../hooks/toast';
import { api } from '../../services/api';
import { ExercisesService } from '../../services/ExercisesService';
import { TrainingsService } from '../../services/TrainingsService';
import { formatSplitLabel } from '../../utils/formatSplitLabel';
import { IExercise, IMuscleGroup } from '../../domains/Exercise';
import { ISuggestedDay, TSuggestTrainingMode } from '../../domains/Training';
import { AddExercisesInTraining } from '../addExercisesInTraining';

import {
  Actions,
  ActionsRight,
  AddExerciseButton,
  Body,
  Container,
  DestructiveWarning,
  EmptyPreview,
  ExerciseRow,
  ExistingLabel,
  FieldLabel,
  FieldRow,
  GhostButton,
  GroupRow,
  GroupsList,
  ModeCard,
  ModeGrid,
  NativeInput,
  NativeSelect,
  PreviewDay,
  PreviewDayHeader,
  PrimaryButton,
  QuantityField,
  QuantityInput,
  RemoveButton,
  StepTitle,
  WarningText,
} from './styles';

interface ISuggestTrainingProps {
  user_avatar?: string;
  user_id: string;
  current_day: number;
  onApplied: () => void;
}

type Step = 'mode' | 'form' | 'preview';

const SPLIT_LABELS = [
  'Split A',
  'Split B',
  'Split C',
  'Split D',
  'Split E',
  'Split F',
  'Split G',
];

// Mesmo fallback de `addExercisesInTraining` -- só pro instante antes do
// fetch de trainings/gym/settings terminar.
const FALLBACK_TRAINING_PARAMS = {
  sets_min: 3,
  sets_max: 4,
  reps_min: 8,
  reps_max: 12,
  interval_min: 60,
  interval_max: 60,
};

const exerciseKey = (splitIndex: number, exerciseId: string) =>
  `${splitIndex}-${exerciseId}`;

const SuggestTraining: React.FC<ISuggestTrainingProps> = ({
  user_avatar,
  user_id,
  current_day,
  onApplied,
}) => {
  const { addModal, removeModal } = useModal();
  const { addToast } = useToast();
  const trainingsService = new TrainingsService();
  const exercisesService = new ExercisesService();

  const [step, setStep] = useState<Step>('mode');
  const [mode, setMode] = useState<TSuggestTrainingMode | null>(null);

  const [muscleGroups, setMuscleGroups] = useState<IMuscleGroup[]>([]);
  const [splitIndex, setSplitIndex] = useState(current_day);
  const [selectedGroups, setSelectedGroups] = useState<
    Record<string, { checked: boolean; quantity: string }>
  >({});
  const [daysPerWeek, setDaysPerWeek] = useState(3);

  const [params, setParams] = useState(FALLBACK_TRAINING_PARAMS);

  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  const [days, setDays] = useState<ISuggestedDay[]>([]);
  const [removedKeys, setRemovedKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    exercisesService
      .getMuscleGroups()
      .then(response => setMuscleGroups(response.data))
      .catch(() => undefined);

    api
      .get('trainings/gym/settings')
      .then(response => {
        setParams({
          sets_min: response.data.default_sets_min,
          sets_max: response.data.default_sets_max,
          reps_min: response.data.default_reps_min,
          reps_max: response.data.default_reps_max,
          interval_min: response.data.default_interval_min,
          interval_max: response.data.default_interval_min,
        });
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleGroup = (code: string) => {
    setSelectedGroups(current => ({
      ...current,
      [code]: {
        checked: !current[code]?.checked,
        quantity: current[code]?.quantity || '',
      },
    }));
  };

  const setGroupQuantity = (code: string, quantity: string) => {
    setSelectedGroups(current => ({
      ...current,
      [code]: { checked: current[code]?.checked ?? true, quantity },
    }));
  };

  const updateParam =
    (field: keyof typeof params) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number(event.target.value);
      setParams(current => ({
        ...current,
        [field]: Number.isNaN(value) ? 0 : value,
      }));
    };

  const hasSelectedGroup = Object.values(selectedGroups).some(
    group => group.checked,
  );

  // Campo em branco vira 0 (ver updateParam) -- 0 nos DOIS lados de um
  // par não faz sentido (nenhum valor sugerido), mas 0 em só UM lado
  // significa "só quero um valor fixo, não uma faixa": o outro lado
  // (preenchido) vale pros dois, em vez de sobrar um "0" literal na
  // ficha gerada.
  const normalizePair = (min: number, max: number): [number, number] => {
    if (min === 0 && max > 0) return [max, max];
    if (max === 0 && min > 0) return [min, min];
    return [min, max];
  };

  const getNormalizedParams = useCallback(() => {
    const [sets_min, sets_max] = normalizePair(
      params.sets_min,
      params.sets_max,
    );
    const [reps_min, reps_max] = normalizePair(
      params.reps_min,
      params.reps_max,
    );
    const [interval_min, interval_max] = normalizePair(
      params.interval_min,
      params.interval_max,
    );

    return {
      sets_min,
      sets_max,
      reps_min,
      reps_max,
      interval_min,
      interval_max,
    };
  }, [params]);

  const buildSuggestPayload = useCallback(() => {
    const normalizedParams = getNormalizedParams();

    if (mode === 'add_to_day') {
      const groups = Object.entries(selectedGroups)
        .filter(([, value]) => value.checked)
        .map(([code, value]) => ({
          muscle_group_code: code,
          quantity: value.quantity ? Number(value.quantity) : undefined,
        }));

      return {
        user_id,
        mode: mode as TSuggestTrainingMode,
        ...normalizedParams,
        split_index: splitIndex,
        groups,
      };
    }

    return {
      user_id,
      mode: mode as TSuggestTrainingMode,
      ...normalizedParams,
      days_per_week: daysPerWeek,
    };
  }, [
    mode,
    selectedGroups,
    getNormalizedParams,
    splitIndex,
    daysPerWeek,
    user_id,
  ]);

  const handleGenerate = useCallback(async () => {
    setGenerating(true);

    try {
      const payload = buildSuggestPayload();
      const response = await trainingsService.suggestTrainingExercises(payload);

      setDays(response.data);
      setRemovedKeys(new Set());
      setStep('preview');
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro ao gerar sugestão',
        description: 'Tente novamente em instantes.',
      });
    } finally {
      setGenerating(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildSuggestPayload]);

  const toggleRemove = (splitIdx: number, exerciseId: string) => {
    setRemovedKeys(current => {
      const next = new Set(current);
      const key = exerciseKey(splitIdx, exerciseId);

      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      return next;
    });
  };

  const handleAddExercise = useCallback(
    (day: ISuggestedDay) => {
      const alreadyInDay: IExercise[] = [
        ...day.exercises.map(item => item.exercise),
        ...(day.existing_exercises?.map(training => training.exercise) || []),
      ];
      const normalizedParams = getNormalizedParams();

      addModal(
        <AddExercisesInTraining
          training_number={day.split_index}
          exercises_already_registered_of_day={alreadyInDay}
          fromSuggestion
          onAdd={async ({
            exercise,
            sets_min,
            sets_max,
            reps_min,
            reps_max,
            interval_min,
            interval_max,
          }) => {
            setDays(current =>
              current.map(item =>
                item.split_index === day.split_index
                  ? {
                      ...item,
                      exercises: [
                        ...item.exercises,
                        {
                          exercise,
                          sets_min: sets_min ?? normalizedParams.sets_min,
                          sets_max: sets_max ?? normalizedParams.sets_max,
                          reps_min: reps_min ?? normalizedParams.reps_min,
                          reps_max: reps_max ?? normalizedParams.reps_max,
                          interval_min:
                            interval_min ?? normalizedParams.interval_min,
                          interval_max:
                            interval_max ?? normalizedParams.interval_max,
                        },
                      ],
                    }
                  : item,
              ),
            );
          }}
        />,
      );
    },
    [addModal, getNormalizedParams],
  );

  const totalRemaining = days.reduce(
    (sum, day) =>
      sum +
      day.exercises.filter(
        item =>
          !removedKeys.has(exerciseKey(day.split_index, item.exercise.id)),
      ).length,
    0,
  );

  const handleSave = useCallback(async () => {
    if (!mode) return;

    setSaving(true);

    try {
      await trainingsService.applySuggestedTraining({
        user_id,
        mode,
        days: days.map(day => ({
          split_index: day.split_index,
          exercises: day.exercises
            .filter(
              item =>
                !removedKeys.has(
                  exerciseKey(day.split_index, item.exercise.id),
                ),
            )
            .map(item => ({
              exercise_id: item.exercise.id,
              sets_min: item.sets_min,
              sets_max: item.sets_max,
              reps_min: item.reps_min,
              reps_max: item.reps_max,
              interval_min: item.interval_min,
              interval_max: item.interval_max,
            })),
        })),
      });

      addToast({ type: 'success', title: 'Treino sugerido salvo.' });
      onApplied();
      removeModal();
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro ao salvar',
        description: 'Tente novamente em instantes.',
      });
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, days, removedKeys, user_id, onApplied, removeModal]);

  return (
    <Container>
      <ModalHeader title="Sugerir treino" avatarUrl={user_avatar} />

      {step === 'mode' && (
        <>
          <Body>
            <StepTitle>Como você quer gerar o treino?</StepTitle>
            <ModeGrid>
              <ModeCard
                type="button"
                selected={mode === 'add_to_day'}
                onClick={() => setMode('add_to_day')}
              >
                <strong>Adicionar a um dia</strong>
                <span>
                  Escolha um dia e um ou mais grupos musculares -- os exercícios
                  sugeridos são somados ao que já existe, nada é apagado.
                </span>
              </ModeCard>
              <ModeCard
                type="button"
                selected={mode === 'reset_full_plan'}
                onClick={() => setMode('reset_full_plan')}
              >
                <strong>Zerar e preencher tudo</strong>
                <span>
                  Escolha quantos dias por semana o aluno treina -- o sistema
                  decide a divisão e substitui os exercícios desses dias.
                </span>
              </ModeCard>
            </ModeGrid>
          </Body>
          <Actions>
            <div />
            <ActionsRight>
              <PrimaryButton
                type="button"
                disabled={!mode}
                onClick={() => setStep('form')}
              >
                Continuar
              </PrimaryButton>
            </ActionsRight>
          </Actions>
        </>
      )}

      {step === 'form' && (
        <>
          <Body>
            {mode === 'add_to_day' ? (
              <>
                <div>
                  <FieldLabel>Dia</FieldLabel>
                  <NativeSelect
                    value={splitIndex}
                    onChange={e => setSplitIndex(Number(e.target.value))}
                  >
                    {SPLIT_LABELS.map((label, index) => (
                      <option key={label} value={index}>
                        {label}
                      </option>
                    ))}
                  </NativeSelect>
                </div>

                <div>
                  <FieldLabel>Grupos musculares (marque um ou mais)</FieldLabel>
                  <GroupsList>
                    {muscleGroups.map(group => {
                      const code = group.code || '';
                      const state = selectedGroups[code];

                      return (
                        <GroupRow key={group.id}>
                          <input
                            type="checkbox"
                            checked={!!state?.checked}
                            onChange={() => toggleGroup(code)}
                          />
                          <span>{group.name}</span>
                          {state?.checked && (
                            <QuantityField>
                              Qtd.
                              <QuantityInput
                                type="number"
                                min={1}
                                title="Quantidade de exercícios pra este grupo (em branco = automático)"
                                value={state.quantity}
                                onChange={e =>
                                  setGroupQuantity(code, e.target.value)
                                }
                              />
                            </QuantityField>
                          )}
                        </GroupRow>
                      );
                    })}
                  </GroupsList>
                </div>
              </>
            ) : (
              <div>
                <FieldLabel>Dias por semana</FieldLabel>
                <NativeSelect
                  value={daysPerWeek}
                  onChange={e => setDaysPerWeek(Number(e.target.value))}
                >
                  {[1, 2, 3, 4, 5, 6].map(n => (
                    <option key={n} value={n}>
                      {n} {n === 1 ? 'dia' : 'dias'}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            )}

            <div>
              <StepTitle>Séries, repetições e descanso</StepTitle>
              <FieldRow style={{ marginTop: 10 }}>
                <div>
                  <FieldLabel>Séries (mín.)</FieldLabel>
                  <NativeInput
                    type="number"
                    value={params.sets_min}
                    onChange={updateParam('sets_min')}
                  />
                </div>
                <div>
                  <FieldLabel>Séries (máx.)</FieldLabel>
                  <NativeInput
                    type="number"
                    value={params.sets_max}
                    onChange={updateParam('sets_max')}
                  />
                </div>
              </FieldRow>
              <FieldRow style={{ marginTop: 10 }}>
                <div>
                  <FieldLabel>Repetições (mín.)</FieldLabel>
                  <NativeInput
                    type="number"
                    value={params.reps_min}
                    onChange={updateParam('reps_min')}
                  />
                </div>
                <div>
                  <FieldLabel>Repetições (máx.)</FieldLabel>
                  <NativeInput
                    type="number"
                    value={params.reps_max}
                    onChange={updateParam('reps_max')}
                  />
                </div>
              </FieldRow>
              <FieldRow style={{ marginTop: 10 }}>
                <div>
                  <FieldLabel>Descanso mín. (s)</FieldLabel>
                  <NativeInput
                    type="number"
                    value={params.interval_min}
                    onChange={updateParam('interval_min')}
                  />
                </div>
                <div>
                  <FieldLabel>Descanso máx. (s)</FieldLabel>
                  <NativeInput
                    type="number"
                    value={params.interval_max}
                    onChange={updateParam('interval_max')}
                  />
                </div>
              </FieldRow>
            </div>
          </Body>
          <Actions>
            <GhostButton type="button" onClick={() => setStep('mode')}>
              Voltar
            </GhostButton>
            <ActionsRight>
              <PrimaryButton
                type="button"
                disabled={
                  generating || (mode === 'add_to_day' && !hasSelectedGroup)
                }
                onClick={handleGenerate}
              >
                {generating ? 'Gerando...' : 'Gerar sugestão'}
              </PrimaryButton>
            </ActionsRight>
          </Actions>
        </>
      )}

      {step === 'preview' && (
        <>
          <Body>
            {mode === 'reset_full_plan' && (
              <DestructiveWarning>
                Isso vai substituir os exercícios já cadastrados em{' '}
                {days
                  .map(day => formatSplitLabel(day.split_index).title)
                  .join(', ')}
                .
              </DestructiveWarning>
            )}

            {days.map(day => {
              const label = formatSplitLabel(day.split_index).title;

              return (
                <PreviewDay key={day.split_index}>
                  <PreviewDayHeader>
                    <strong>{label}</strong>
                    <span>{day.muscle_groups.join(', ')}</span>
                  </PreviewDayHeader>

                  {!!day.existing_exercises?.length && (
                    <div>
                      <ExistingLabel>
                        Exercícios já presentes no treino
                      </ExistingLabel>
                      {day.existing_exercises.map(training => (
                        <ExerciseRow key={training.id} dimmed>
                          <span>{training.exercise.name}</span>
                        </ExerciseRow>
                      ))}
                    </div>
                  )}

                  {day.warnings.length > 0 && (
                    <WarningText>
                      Nenhum exercício suficiente encontrado pra:{' '}
                      {day.warnings.join(', ')}
                    </WarningText>
                  )}

                  {day.exercises.length === 0 ? (
                    <EmptyPreview>
                      Nenhum exercício sugerido pra este dia.
                    </EmptyPreview>
                  ) : (
                    day.exercises.map(item => {
                      const key = exerciseKey(
                        day.split_index,
                        item.exercise.id,
                      );
                      const removed = removedKeys.has(key);
                      const mainGroup =
                        item.exercise.muscle_groups?.find(
                          group => group.is_main,
                        ) || item.exercise.muscle_groups?.[0];

                      return (
                        <ExerciseRow key={item.exercise.id} dimmed={removed}>
                          <span>
                            {item.exercise.name}{' '}
                            <span className="muscle">
                              {mainGroup?.name || mainGroup?.muscle_group?.name}
                            </span>
                          </span>
                          <RemoveButton
                            type="button"
                            onClick={() =>
                              toggleRemove(day.split_index, item.exercise.id)
                            }
                          >
                            <Icon
                              name={removed ? 'add' : 'close'}
                              size={16}
                              color="currentColor"
                            />
                          </RemoveButton>
                        </ExerciseRow>
                      );
                    })
                  )}

                  <AddExerciseButton
                    type="button"
                    onClick={() => handleAddExercise(day)}
                  >
                    <Icon name="add" size={15} color="currentColor" />
                    Adicionar exercício
                  </AddExerciseButton>
                </PreviewDay>
              );
            })}
          </Body>
          <Actions>
            <GhostButton type="button" onClick={() => setStep('form')}>
              Voltar
            </GhostButton>
            <ActionsRight>
              <GhostButton
                type="button"
                disabled={generating}
                onClick={handleGenerate}
              >
                {generating ? 'Gerando...' : 'Gerar de novo'}
              </GhostButton>
              <PrimaryButton
                type="button"
                disabled={saving || totalRemaining === 0}
                onClick={handleSave}
              >
                {saving ? 'Salvando...' : 'Salvar treino sugerido'}
              </PrimaryButton>
            </ActionsRight>
          </Actions>
        </>
      )}
    </Container>
  );
};

export { SuggestTraining };
